import { useEffect, useState, useCallback, useRef } from "react";
import { useDolfinClient } from "@/components/DolfinProvider";
import { usePermissions } from "@/lib/permissions";
import { useIsDemo } from "@/demo/DemoContext";
import { apiErrorMessage } from "@/lib/api-error";
import { recurringScheduleStatusValues, type components } from "@dolfin/api-public";
import { enumOptions } from "@/lib/enum-options";
import { EnumMultiSelect } from "@/components/EnumMultiSelect";
import { Button } from "@/components/ui/button";
import { PermissionGuard } from "@/components/PermissionGuard";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  EmptyState,
  EntityCell,
  ErrorState,
  HeaderRow,
  LoadingState,
  Pager,
  TH,
  TableShell,
  rowLink,
} from "@/components/PageUi";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Spinner } from "@/components/ui/spinner";
import { Ban, MoreHorizontal, Pause, Play, Plus, Repeat, Upload } from "lucide-react";
import { daysFromToday, formatDate } from "@/lib/format";
import { PageContainer, PageHeader } from "@/components/PagePrimitives";
import { CADENCE_NOUN, ScheduleStatusPill, scheduleProgress } from "@/components/ScheduleShared";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

type ScheduleResponse = components["schemas"]["RecurringInvoiceScheduleResponse"];
type RecurringScheduleStatus = components["schemas"]["RecurringScheduleStatus"];
type ExtractedContractTerms = components["schemas"]["ExtractedContractTerms"];

const STATUSES = enumOptions(recurringScheduleStatusValues);

const PAGE_SIZE = 10;
// Backend caps contract uploads at 5 MB; reject earlier so the user gets a clear message.
const MAX_CONTRACT_BYTES = 5 * 1024 * 1024;

/** Whole days from today to a date, in words: "Today", "Tomorrow", "In 5 days", "3 days ago". */
export function relativeDays(dateStr: string | null | undefined): string | null {
  if (!dateStr) return null;
  const days = daysFromToday(dateStr);
  if (days == null) return null;
  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";
  if (days === -1) return "Yesterday";
  return days > 0 ? `In ${days} days` : `${-days} days ago`;
}

/** A bar only when the run has an end; an open-ended schedule reads as a count. */
export function ScheduleProgressMeter({ schedule }: { schedule: ScheduleResponse }) {
  const total = Number(schedule.totalOccurrences ?? 0);
  const generated = Number(schedule.occurrencesGenerated ?? 0);
  if (!total) {
    return <span className="text-sm tabular-nums text-muted-foreground">{scheduleProgress(schedule)}</span>;
  }
  const pct = Math.max(0, Math.min(100, Math.round((generated / total) * 100)));
  return (
    <div className="ml-auto w-24" aria-label={`${generated} of ${total} invoices generated`}>
      <div className="text-right text-sm tabular-nums">{scheduleProgress(schedule)}</div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted">
        <div className="h-full rounded-full bg-muted-foreground/50" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export interface SchedulesListFilters {
  /** Zero or more schedule statuses to filter by. Empty/undefined = all. */
  statuses?: RecurringScheduleStatus[];
}

export interface SchedulesListPageProps {
  onScheduleClick: (id: string) => void;
  onCreateSchedule: () => void;
  onCustomerClick: (id: string) => void;
  /** Called with extracted contract terms, so the host can open create-schedule prefilled. */
  onContractExtracted?: (terms: ExtractedContractTerms) => void;
  /** Initial filters. Updates to filters live in component state. */
  defaultFilters?: SchedulesListFilters;
  onFiltersChange?: (filters: SchedulesListFilters) => void;
}

export function SchedulesListPage({
  onScheduleClick,
  onCreateSchedule,
  onCustomerClick,
  onContractExtracted,
  defaultFilters,
  onFiltersChange,
}: SchedulesListPageProps) {
  const api = useDolfinClient();
  const { can } = usePermissions();
  const canManage = can("manageInvoices");
  const isDemo = useIsDemo();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const [schedules, setSchedules] = useState<ScheduleResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  const [actingId, setActingId] = useState<string | null>(null);
  const [scheduleToCancel, setScheduleToCancel] = useState<ScheduleResponse | null>(null);

  const [statusFilter, setStatusFilter] = useState<RecurringScheduleStatus[]>(
    defaultFilters?.statuses ?? [],
  );

  // Same emit-guard pattern as invoices-list-page: see the comment there.
  const onFiltersChangeRef = useRef(onFiltersChange);
  onFiltersChangeRef.current = onFiltersChange;
  const firstEmit = useRef(true);

  useEffect(() => {
    if (firstEmit.current) {
      firstEmit.current = false;
      return;
    }
    onFiltersChangeRef.current?.({
      statuses: statusFilter.length > 0 ? statusFilter : undefined,
    });
  }, [statusFilter]);

  const fetchSchedules = useCallback(
    async (cursor?: string) => {
      setLoading(true);
      setError(null);

      try {
        const query: Record<string, string | number | RecurringScheduleStatus[]> = {
          Limit: PAGE_SIZE,
        };
        if (statusFilter.length > 0) query.Statuses = statusFilter;
        if (cursor) query.Cursor = cursor;

        const { data, error: apiError } = await api.GET("/v1/recurring-invoices", {
          params: { query },
        });

        if (apiError || !data) {
          console.error("Failed to load schedules:", apiError);
          setError("Failed to load recurring invoices");
          return;
        }

        setSchedules(data.data ?? []);
        const next = data.meta?.cursors?.next ?? null;
        setNextCursor(next);
        setHasMore(next !== null);
      } catch (e) {
        console.error("Failed to load schedules:", e);
        setError("Failed to load recurring invoices");
      } finally {
        setLoading(false);
      }
    },
    [api, statusFilter],
  );

  useEffect(() => {
    setCursorStack([]);
    setNextCursor(null);
    fetchSchedules();
  }, [fetchSchedules]);

  function handleNextPage() {
    if (!nextCursor) return;
    setCursorStack((prev) => [...prev, nextCursor]);
    fetchSchedules(nextCursor);
  }

  function handlePrevPage() {
    const newStack = [...cursorStack];
    newStack.pop();
    const prevCursor = newStack[newStack.length - 1];
    setCursorStack(newStack);
    fetchSchedules(prevCursor);
  }

  async function pauseOrResume(schedule: ScheduleResponse, action: "pause" | "resume") {
    if (!schedule.id) return;
    setActingId(schedule.id);
    const { error: apiError } =
      action === "pause"
        ? await api.POST("/v1/recurring-invoices/{id}/pause", {
            params: { path: { id: schedule.id } },
          })
        : await api.POST("/v1/recurring-invoices/{id}/resume", {
            params: { path: { id: schedule.id } },
          });
    setActingId(null);

    if (apiError) {
      toast.error(apiErrorMessage(apiError, `Failed to ${action} recurring invoice`));
      return;
    }

    toast.success(
      action === "pause" ? "Recurring invoice paused" : "Recurring invoice resumed",
    );
    fetchSchedules(cursorStack[cursorStack.length - 1]);
  }

  async function confirmCancel() {
    const target = scheduleToCancel;
    if (!target?.id) return;
    const id = target.id;
    setScheduleToCancel(null);
    setActingId(id);
    const { error: apiError } = await api.POST("/v1/recurring-invoices/{id}/cancel", {
      params: { path: { id } },
    });
    setActingId(null);

    if (apiError) {
      toast.error(apiErrorMessage(apiError, "Failed to cancel recurring invoice"));
      return;
    }

    toast.success("Recurring invoice cancelled");
    fetchSchedules(cursorStack[cursorStack.length - 1]);
  }

  async function handleContractFile(file: File) {
    if (file.size > MAX_CONTRACT_BYTES) {
      toast.error("Contract is too large - the limit is 5 MB.");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const { data, error: apiError } = await api.POST(
        "/v1/recurring-invoices/from-contract",
        {
          // openapi-fetch requires bodySerializer pass-through for multipart payloads
          body: formData as unknown as never,
          bodySerializer: (body) => body as BodyInit,
        },
      );

      if (apiError || !data) {
        toast.error(apiErrorMessage(apiError, "Couldn't read that contract."));
        return;
      }

      onContractExtracted?.(data);
    } catch (e) {
      console.error("Failed to extract contract:", e);
      toast.error("Couldn't read that contract.");
    } finally {
      setUploading(false);
    }
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    if (uploading) return;
    // Single upload only: take the first file if several are dropped.
    const file = e.dataTransfer.files?.[0];
    if (file) handleContractFile(file);
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
    if (!uploading) setDragOver(true);
  }

  const currentPage = cursorStack.length + 1;

  // A demo answers Demo.ReadOnly: contract extraction is a paid LLM call with no row to count against a cap.
  const canUpload = canManage && !!onContractExtracted && !isDemo;

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Receivables"
        title="Recurring invoices"
        subtitle={
          !loading
            ? "Invoices generated on a cadence, sent automatically or left as drafts for review."
            : undefined
        }
        actions={
          <>
            <EnumMultiSelect
              options={STATUSES}
              value={statusFilter}
              onChange={setStatusFilter}
              placeholder="Status"
              searchPlaceholder="Search statuses..."
              className="w-full sm:w-[200px]"
            />
            <PermissionGuard allowed={canManage} reason="Only owners and admins can create recurring invoices" className="w-full sm:w-auto">
              <Button onClick={onCreateSchedule} className="w-full gap-1.5 sm:w-auto">
                <Plus className="size-4" />
                New recurring invoice
              </Button>
            </PermissionGuard>
          </>
        }
      />

      {canUpload && (
        <div
          role="button"
          tabIndex={0}
          aria-label="Upload a contract"
          className={cn(
            "mb-5 rounded-xl border-2 border-dashed px-6 py-5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            dragOver
              ? "border-accent-blue/60 bg-accent-blue/5"
              : "border-muted-foreground/25 hover:border-muted-foreground/50",
            uploading ? "pointer-events-none opacity-70" : "cursor-pointer",
          )}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={() => setDragOver(false)}
          onClick={() => !uploading && fileInputRef.current?.click()}
          onKeyDown={(e) => {
            if ((e.key === "Enter" || e.key === " ") && !uploading) fileInputRef.current?.click();
          }}
        >
          {uploading ? (
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Spinner className="size-4" />
              Reading contract...
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1">
              <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm">
                <Upload className="size-5 text-muted-foreground" />
                <span className="font-medium">Start from a contract</span>
                <span className="text-xs text-muted-foreground">
                  Drop it here, or click to browse · PDF · PNG · JPG · up to 5 MB
                </span>
              </div>
              <p className="max-w-xl text-xs text-pretty text-muted-foreground">
                We read the recurring billing terms and prefill a new recurring invoice for you to
                review before anything is sent.
              </p>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              // Reset so re-selecting the same file fires onChange again.
              e.target.value = "";
              if (file) handleContractFile(file);
            }}
          />
        </div>
      )}

      {error ? (
        <ErrorState
          message={error}
          onRetry={() => fetchSchedules(cursorStack[cursorStack.length - 1])}
        />
      ) : loading ? (
        <LoadingState label="Loading recurring invoices..." />
      ) : schedules.length === 0 ? (
        statusFilter.length > 0 ? (
          <EmptyState
            icon={Repeat}
            title="No recurring invoices match"
            action={
              <Button variant="outline" size="sm" onClick={() => setStatusFilter([])}>
                Clear filter
              </Button>
            }
          >
            Nothing has the selected status. Clear the filter to see every recurring invoice.
          </EmptyState>
        ) : (
          <EmptyState
            icon={Repeat}
            title="No recurring invoices yet"
            action={
              <PermissionGuard allowed={canManage} reason="Only owners and admins can create recurring invoices">
                <Button size="sm" onClick={onCreateSchedule} className="gap-1.5">
                  <Plus className="size-4" />
                  New recurring invoice
                </Button>
              </PermissionGuard>
            }
          >
            Set one up for a customer you bill on a cadence. Each invoice is generated on schedule,
            either sent automatically or left as a draft for you to review.
          </EmptyState>
        )
      ) : (
        <>
          <TableShell>
            <Table>
              <TableHeader>
                <HeaderRow>
                  <TableHead className={TH}>Customer</TableHead>
                  <TableHead className={TH}>Cadence</TableHead>
                  <TableHead className={TH}>Next invoice</TableHead>
                  <TableHead className={cn(TH, "text-right")}>Progress</TableHead>
                  <TableHead className={cn(TH, "w-[100px] text-right")}>Status</TableHead>
                  <TableHead className="w-[48px]" />
                </HeaderRow>
              </TableHeader>
              <TableBody>
                {schedules.map((schedule) => {
                  const isActing = actingId === schedule.id;
                  const showPause = canManage && schedule.status === "Active";
                  const showResume = canManage && schedule.status === "Paused";
                  const showCancel =
                    canManage &&
                    (schedule.status === "Active" || schedule.status === "Paused");
                  const showMenu = showPause || showResume || showCancel || !!schedule.customerId;
                  const lineCount = schedule.lineItems?.length ?? 0;
                  const link = rowLink(() => schedule.id && onScheduleClick(schedule.id));
                  return (
                    <TableRow key={schedule.id} {...link}>
                      <TableCell>
                        <EntityCell
                          name={schedule.customer?.name}
                          fallback="Unknown customer"
                          secondary={
                            schedule.memo ||
                            `${lineCount} ${lineCount === 1 ? "line" : "lines"} · ${schedule.currency}`
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{schedule.cadence ?? "-"}</div>
                        <div className="text-xs text-muted-foreground">
                          {schedule.startDate
                            ? `Every ${CADENCE_NOUN[schedule.cadence ?? "Monthly"]} from ${formatDate(schedule.startDate)}`
                            : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        {schedule.status === "Active" && schedule.nextRunAt ? (
                          <>
                            <div className="text-sm tabular-nums">{formatDate(schedule.nextRunAt)}</div>
                            <div className="text-xs text-muted-foreground">
                              {relativeDays(schedule.nextRunAt)}
                              {schedule.autoSend ? " · sent automatically" : " · as a draft"}
                            </div>
                          </>
                        ) : (
                          <span className="text-sm text-muted-foreground">
                            {schedule.status === "Paused" ? "Paused" : "-"}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <ScheduleProgressMeter schedule={schedule} />
                      </TableCell>
                      <TableCell className="text-right">
                        <ScheduleStatusPill status={schedule.status ?? undefined} />
                      </TableCell>
                      <TableCell className="text-right">
                        {showMenu && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                disabled={isActing}
                                className="size-8"
                                aria-label="Recurring invoice actions"
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => e.stopPropagation()}
                              >
                                {isActing ? (
                                  <Spinner className="size-4" />
                                ) : (
                                  <MoreHorizontal className="size-4" />
                                )}
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align="end"
                              className="w-48"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {schedule.customerId && (
                                <DropdownMenuItem
                                  onClick={() => onCustomerClick(schedule.customerId!)}
                                >
                                  Open customer
                                </DropdownMenuItem>
                              )}
                              {showPause && (
                                <DropdownMenuItem
                                  onClick={() => pauseOrResume(schedule, "pause")}
                                >
                                  <Pause className="mr-2 size-4" />
                                  Pause schedule
                                </DropdownMenuItem>
                              )}
                              {showResume && (
                                <DropdownMenuItem
                                  onClick={() => pauseOrResume(schedule, "resume")}
                                >
                                  <Play className="mr-2 size-4" />
                                  Resume schedule
                                </DropdownMenuItem>
                              )}
                              {showCancel && (
                                <DropdownMenuItem
                                  className="text-destructive focus:text-destructive"
                                  onClick={() => setScheduleToCancel(schedule)}
                                >
                                  <Ban className="mr-2 size-4" />
                                  Cancel schedule
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableShell>

          <Pager
            page={currentPage}
            hasPrevious={currentPage > 1}
            hasNext={hasMore}
            onPrevious={handlePrevPage}
            onNext={handleNextPage}
          />
        </>
      )}

      <AlertDialog
        open={scheduleToCancel !== null}
        onOpenChange={(open) => !open && setScheduleToCancel(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel recurring invoice?</AlertDialogTitle>
            <AlertDialogDescription>
              The recurring invoice for {scheduleToCancel?.customer?.name ?? "this customer"}{" "}
              will stop generating invoices permanently - a cancelled recurring invoice cannot
              be resumed. Invoices it has already generated are unaffected. If the stop might be
              temporary, pause it instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep recurring invoice</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={confirmCancel}>
              Cancel recurring invoice
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
}
